import { useApiClient } from '@/composables/api-client'
import { createQuery, createUrl } from '@/utils/pinia/helpers'
import { defaultItemsStoreQuery } from '@/utils/pinia/defaults'
import { createItemsStoreWrapper } from '~/utils/pinia/items-store-setup'
import type { ItemsStoreQuery } from '~/utils/pinia/types'
import type {
  FantasyLeaderboardEntry,
  FantasyLeaderboardSection,
  FantasySeasonLeaderboardResponse
} from '~/types/fantasy'
import {
  normalizeLeaderboardEntry,
  normalizeLeaderboardSections
} from '~/utils/fantasy/leaderboard'

const createItemsStore = createItemsStoreWrapper<
  'id',
  FantasyLeaderboardEntry
>()

const storeOptions = {
  path: (_query: ItemsStoreQuery, options: Record<string, unknown>) =>
    `/api/v1/fantasy/season/${options.seasonId}/leaderboard`,
  idKey: 'id' as const,
  pagination: true,
  paginationType: 'page' as const,
  query: {
    expand: ['member.styles']
  }
}

const defaultQuery = Object.assign(
  {},
  defaultItemsStoreQuery,
  storeOptions.query
)

export const useFantasyLeaderboardStore = createItemsStore(
  'fantasy-leaderboard',
  storeOptions,
  context => {
    const sections = ref<FantasyLeaderboardSection[]>([])
    const me = ref<FantasyLeaderboardEntry | null>(null)
    const seasonId = ref<number | null>(null)

    const fetchPage = async (
      id: number,
      requestQuery: ItemsStoreQuery = {}
    ) => {
      const client = await useApiClient()
      const mergedQuery = Object.assign({}, defaultQuery, requestQuery)
      const queryToRequest = createQuery(storeOptions, mergedQuery)
      const url = createUrl(storeOptions.path, mergedQuery, { seasonId: id })

      return client.get<FantasySeasonLeaderboardResponse>(url, {
        query: queryToRequest
      })
    }

    const fetchItems = async (
      id: number,
      requestQuery: ItemsStoreQuery = {}
    ) => {
      seasonId.value = id
      context.reset()
      sections.value = []
      me.value = null

      const mergedQuery = Object.assign(
        {},
        defaultQuery,
        { page: 1 },
        requestQuery
      )
      const result = await fetchPage(id, mergedQuery)

      context.items.value = result.items.map(normalizeLeaderboardEntry)
      context.query.value = mergedQuery
      context.url.value = createUrl(storeOptions.path, mergedQuery, {
        seasonId: id
      })
      context.isLastPage.value = result._meta.pageCount < 2
      sections.value = normalizeLeaderboardSections(result.sections)
      me.value = result.me ? normalizeLeaderboardEntry(result.me) : null

      return context.items.value
    }

    const fetchMoreItems = async () => {
      if (!seasonId.value || context.isLastPage.value || !context.query.value) {
        return context.items.value
      }

      const mergedQuery = {
        ...context.query.value,
        page: (context.query.value.page ?? 1) + 1
      }
      const result = await fetchPage(seasonId.value, mergedQuery)

      context.items.value = context.items.value.concat(
        result.items.map(normalizeLeaderboardEntry)
      )
      context.query.value = mergedQuery
      context.isLastPage.value =
        result._meta.currentPage === result._meta.pageCount

      return context.items.value
    }

    return {
      sections,
      me,
      fetchItems,
      fetchMoreItems
    }
  }
)
